/**
 * The HTML sanitiser for stored rich text (KIT_REQUIREMENTS §1.5): task descriptions and comments, message bodies,
 * survey / suggestion / offer descriptions, dashboard previews, public offer pages. Replaces every legacy `v-html`
 * (FX-030, FX-041, NEW-13, NEW-17, NEW-26, NEW-32, job-offers OPEN-14 (b)); owner decisions MESSAGES D2 and
 * SURVEYS D4: the apps sanitise.
 *
 * Allow-list = what `AppRichText` writes (and what Quill 1 wrote for the same toolbars) + safe links:
 * - tags: `p br strong em u s sub sup span a ol ul li h1–h6`, plus `b i` (the API's own markup, e.g. the voice
 *   command answer) — everything else is removed and its text kept (`script`, `style`, `iframe`… lose their content);
 * - attributes: `href` (http, https, mailto, tel or relative), `target` (`_blank` only), `class` (Quill's own
 *   `ql-align|indent|size|font|direction-*` only); every link opens in a new tab with `rel="noopener noreferrer"`,
 *   as Quill's did.
 * Images, inline styles, `data-*`, event handlers and `javascript:` / `data:` URLs never survive.
 */
import DOMPurify, { type Config } from 'dompurify'

export const RICH_TEXT_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'sub',
  'sup',
  'span',
  'a',
  'ol',
  'ul',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
] as const

export const RICH_TEXT_ATTRIBUTES = ['href', 'target', 'class'] as const

/** Link schemes the editor accepts and the sanitiser keeps (Quill 1's own whitelist). */
export const SAFE_LINK_PROTOCOLS = ['http', 'https', 'mailto', 'tel'] as const

/** The Quill 1 classes stored content may carry; `AppRichText` round-trips them and `.app-rich-content` styles them. */
export const QUILL_CLASS =
  /^ql-(?:align-(?:center|right|justify)|indent-[1-8]|size-(?:small|large|huge)|font-(?:serif|monospace)|direction-rtl)$/

// DOMPurify's default URI rule with the scheme list narrowed to SAFE_LINK_PROTOCOLS (relative URLs stay allowed).
const SAFE_URI = new RegExp(`^(?:(?:${SAFE_LINK_PROTOCOLS.join('|')}):|[^a-z]|[a-z+.-]+(?:[^a-z+.\\-:]|$))`, 'i')

/** Whether the sanitiser keeps this `href` (the editor refuses the others). */
export function isSafeHref(url: string): boolean {
  const trimmed = url.trim()
  return trimmed !== '' && SAFE_URI.test(trimmed)
}

/** `url` trimmed when it is safe to open (`isSafeHref`), else `''` — a download or link address, whoever wrote it. */
export function safeHref(url: string | null | undefined): string {
  const href = url?.trim() ?? ''
  return href && isSafeHref(href) ? href : ''
}

/**
 * What the user typed in the editor's link field → the stored `href`: `www.x.com` → `https://www.x.com`,
 * `a@b.fr` → `mailto:a@b.fr`; `''` when the result is not a safe link. Quill stored `www.x.com` as is, a link
 * relative to the app.
 */
export function normalizeLink(input: string): string {
  const trimmed = input.trim()
  if (!trimmed) return ''
  let href = trimmed
  if (!/^[a-z][a-z0-9+.-]*:/i.test(trimmed) && !/^[/#?]/.test(trimmed)) {
    href = /^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/.test(trimmed) ? `mailto:${trimmed}` : `https://${trimmed}`
  }
  return isSafeHref(href) ? href : ''
}

const CONFIG: Config = {
  ALLOWED_TAGS: [...RICH_TEXT_TAGS],
  ALLOWED_ATTR: [...RICH_TEXT_ATTRIBUTES],
  ALLOWED_URI_REGEXP: SAFE_URI,
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
}

type Purifier = ReturnType<typeof DOMPurify>

let purifier: Purifier | null = null

// A private instance, so the hooks below never leak into another DOMPurify user.
function instance(): Purifier {
  if (purifier) return purifier
  const created = DOMPurify(window)
  created.addHook('uponSanitizeAttribute', (_node, event) => {
    if (event.attrName === 'class') {
      const kept = event.attrValue.split(/\s+/).filter((token) => QUILL_CLASS.test(token))
      if (kept.length) event.attrValue = kept.join(' ')
      else event.keepAttr = false
    } else if (event.attrName === 'target' && event.attrValue !== '_blank') {
      event.keepAttr = false
    }
  })
  created.addHook('afterSanitizeAttributes', (node) => {
    if (node.nodeName === 'A' && node.hasAttribute('href')) {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    }
  })
  purifier = created
  return created
}

/** Stored rich text → HTML safe for `v-html` (render it through `AppHtml`). `null` / `undefined` → `''`. */
export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return ''
  return instance().sanitize(html, CONFIG)
}

function parse(html: string): HTMLElement {
  return new DOMParser().parseFromString(`<!doctype html><body>${html}</body>`, 'text/html').body
}

/**
 * The text of an HTML string, tags removed and entities decoded — the job offer's AI counter (legacy
 * `parseHtmlToText`, which counted `&amp;` as five characters) and the "is the editor empty" test.
 * Parsing happens in an inert document: nothing loads or runs.
 */
export function htmlToText(html: string | null | undefined): string {
  if (!html) return ''
  return parse(html).textContent ?? ''
}

/** Elements that separate words: a text on each side of one is never the same word (`one</p><p>two`, `one<br>two`). */
const WORD_BREAKS = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'BR',
  'DD',
  'DIV',
  'DL',
  'DT',
  'FIGCAPTION',
  'FIGURE',
  'FOOTER',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'HR',
  'LI',
  'OL',
  'P',
  'PRE',
  'SECTION',
  'TABLE',
  'TD',
  'TH',
  'TR',
  'UL',
])

/** The nearest word-breaking ancestor of a node (its block), else `null` (the body). */
function blockOf(node: Node, body: HTMLElement): Element | null {
  for (let parent = node.parentElement; parent && parent !== body; parent = parent.parentElement)
    if (WORD_BREAKS.has(parent.nodeName)) return parent
  return null
}

/**
 * Sanitised HTML cut after `words` words of its **text** (dashboard previews: legacy cut the HTML string mid-tag,
 * NEW-13 / dashboards OPEN-5 a), with `...` appended when something was cut — legacy `truncate(data, 30)`.
 * Formatting up to the cut is kept; elements after it are dropped. Words are counted on the text as read: a piece
 * glued to the previous one across an inline tag is the same word — the comma in `<strong>two</strong>, three`, the
 * rest of `Hel<b>lo</b>` — while a block or `<br>` always separates two words.
 */
export function truncateHtml(html: string | null | undefined, words: number): string {
  const clean = sanitizeHtml(html)
  if (!clean || words <= 0) return clean
  const body = parse(clean)
  const walker = body.ownerDocument.createTreeWalker(body, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT)
  let seen = 0
  /** Where to cut: after the last piece of the `words`-th word. */
  let cut: { node: Text; offset: number } | null = null
  /** Whether the text read so far ends inside a word that the next text may continue. */
  let glued = false
  let block: Element | null = null
  scan: for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.nodeType !== Node.TEXT_NODE) {
      if (WORD_BREAKS.has(node.nodeName)) glued = false
      continue
    }
    const text = node as Text
    const container = blockOf(text, body)
    if (container !== block) {
      glued = false
      block = container
    }
    for (const match of text.data.matchAll(/\S+/g)) {
      const index = match.index ?? 0
      const continues = glued && index === 0
      if (!continues) {
        if (cut) break scan
        seen += 1
      }
      if (cut || seen === words) cut = { node: text, offset: index + match[0].length }
    }
    if (text.data) glued = !/\s$/.test(text.data)
  }
  if (!cut) return clean
  const rest = body.ownerDocument.createRange()
  rest.setStart(cut.node, cut.offset)
  rest.setEndAfter(body.lastChild ?? cut.node)
  if (!rest.toString().trim()) return clean
  rest.deleteContents()
  cut.node.data = `${cut.node.data.slice(0, cut.offset)}...`
  return body.innerHTML
}

// --- Markdown (renderMarkdown, markdown.ts) ------------------------------------------------------------------------

/** What `renderMarkdown()` writes besides the rich-text tags: code, quotes, rules, strikethrough. */
export const MARKDOWN_TAGS = [...RICH_TEXT_TAGS, 'code', 'pre', 'blockquote', 'hr', 'del'] as const

/** The classes Markdown output may carry: a mention (`md-mention`, `md-mention-me` when it names the viewer). */
export const MARKDOWN_CLASS = /^md-mention(?:-me)?$/

const MARKDOWN_CONFIG: Config = { ...CONFIG, ALLOWED_TAGS: [...MARKDOWN_TAGS] }

let markdownPurifier: Purifier | null = null

function markdownInstance(): Purifier {
  if (markdownPurifier) return markdownPurifier
  const created = DOMPurify(window)
  created.addHook('uponSanitizeAttribute', (_node, event) => {
    if (event.attrName === 'class') {
      const kept = event.attrValue.split(/\s+/).filter((token) => MARKDOWN_CLASS.test(token))
      if (kept.length) event.attrValue = kept.join(' ')
      else event.keepAttr = false
    } else if (event.attrName === 'target') {
      event.keepAttr = false
    }
  })
  created.addHook('afterSanitizeAttributes', (node) => {
    // A mention links inside the app (the page routes it); every other link opens in a new tab.
    if (node.nodeName === 'A' && node.hasAttribute('href') && !node.classList.contains('md-mention')) {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    }
  })
  markdownPurifier = created
  return created
}

/** HTML rendered from Markdown → safe for `innerHTML` (the rich-text allow-list plus code, quotes and mentions). */
export function sanitizeMarkdownHtml(html: string | null | undefined): string {
  if (!html) return ''
  return markdownInstance().sanitize(html, MARKDOWN_CONFIG)
}
